import React, { useState, useEffect } from 'react';
import { Layers, Settings, Plus, Trash2, Loader2, PlusCircle, Save } from 'lucide-react';

import Card from '../components/ui/Card';
import Input from '../components/ui/Input';
import Button from '../components/ui/Button';
import SearchableSelectBox from '../components/ui/SearchableSelectBox';
import Modal from '../components/ui/Modal';

import { useToast } from '../hooks/useToast';
import { client, fetchInventoryForStoreSales, fetchTargetsForProduction, fetchServicesForStoreSale, fetchStoresWhereStaffAssigned } from '../apiHelper';

const WORKCENTER_RESOURCE_TYPES = [
    { label: "CNC / Machining Center", value: "cnc_machining" },
    { label: "Cutting & Sawing Station", value: "cutting_sawing" },
    { label: "Assembly Line / Workcell", value: "assembly_line" },
    { label: "Welding & Fabrication Bay", value: "welding_fab" },
    { label: "Quality Assurance & Inspection", value: "qa_inspection" },
    { label: "Manual Benchwork / Hand Assembly", value: "manual_labor" }
];

export const ProductSpecsManager = ({ storeId, currentUser }) => {
    const { showToast } = useToast();
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const [productOptions, setProductOptions] = useState([]);
    const [workcenterOptions, setWorkcenterOptions] = useState([]);
    const [isWcModalOpen, setIsWcModalOpen] = useState(false);

    const [allStores, setAllStores] = useState([]);
    const [componentOptions, setComponentOptions] = useState([]);
    const [loadingComponents, setLoadingComponents] = useState(false);


    const [specData, setSpecData] = useState({
        catentryId: '',
        bomVersion: 1,
        bomDescription: null,
        routingVersion: 1,
        routingDescription: null,
        bomLines: [],
        routingOperations: []
    });

    const [currentBomLine, setCurrentBomLine] = useState({
        storeId: '',
        componentCatentryId: '',
        quantity: '',
        unitMeasure: 'PCE',
        scrapFactor: '0'
    });

    const [currentOp, setCurrentOp] = useState({
        workcenterId: '',
        sequence: '',
        description: '',
        setupTime: '',
        runTimePerUnit: '',
        laborCostPerHour: ''
    });

    const [newWcList, setNewWcList] = useState([
        { name: '', resource_type: '', capacity_per_hour: '', cost_per_hour: '' }
    ]);
    const [creatingWc, setCreatingWc] = useState(false);

    useEffect(() => {
        if (!specData.catentryId) return;

        const checkVersionRedundancy = async () => {
            const selectedProduct = productOptions.find(p => p.value === specData.catentryId);
            const selectedStore = allStores.find(s => s.value === storeId.toString());

            const productName = selectedProduct ? selectedProduct.label : `Product #${specData.catentryId}`;
            const storeName = selectedStore ? selectedStore.label : `Store #${storeId}`;

            const descriptions = {
                bom_description: specData.bomDescription
                    || `${productName} - ${storeName} (BOM v${specData.bomVersion})`,
                routing_description: specData.routingDescription
                    || `${productName} - ${storeName} (Routing Rev ${specData.routingVersion})`
            };

            // console.log("Packaged Descriptions:", {
            //     bd_description: descriptions.bom_description,
            //     rh_description: descriptions.routing_description
            // });

            try {
                const response = await client.postData('/manufacturing/version-redundancy', {
                    catentry_id: parseInt(specData.catentryId, 10),
                    store_id: parseInt(storeId, 10),
                    bd_description: descriptions.bom_description,
                    rh_description: descriptions.routing_description
                });

                if (response?.msg === 'yes') {
                    showToast("Incrementing BOM and Routing revision levels.", "info");
                    setSpecData(prev => ({
                        ...prev,
                        bomVersion: Number(prev.bomVersion) + 1,
                        routingVersion: Number(prev.routingVersion) + 1
                    }));
                }
            } catch (err) {
                console.error("Error checking version redundancy:", err);
            }
        };

        checkVersionRedundancy();
    }, [specData.catentryId, specData.bomVersion, specData.routingVersion, storeId, productOptions, allStores]);

    const handleAddWcRow = () => {
        setNewWcList(prev => [
            ...prev,
            { name: '', resource_type: '', capacity_per_hour: '', cost_per_hour: '' }
        ]);
    };

    const handleRemoveWcRow = (index) => {
        if (newWcList.length === 1) return; // Keep at least one row
        setNewWcList(prev => prev.filter((_, i) => i !== index));
    };

    const handleWcRowChange = (index, field, value) => {
        setNewWcList(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [field]: value };
            return updated;
        });
    };

    // useEffect FOR FETCHING BOM COMPONENTS BY STORE:
    useEffect(() => {
        if (!currentBomLine.storeId) {
            setComponentOptions([]);
            return;
        }
        const loadComponentsForStore = async () => {
            setLoadingComponents(true);
            try {
                const payload = { ...currentUser, store_id: parseInt(currentBomLine.storeId, 10), offset: 0, limit: 10000 };
                const [services, inventory] = await Promise.all([
                    fetchServicesForStoreSale(payload).catch(() => []),
                    fetchInventoryForStoreSales(payload).catch(() => [])
                ]);

                const combined = [...services, ...inventory];
                const itemMap = new Map();
                combined.forEach(lot => {
                    if (lot?.catentry) {
                        itemMap.set(lot.catentry.catentry_id, {
                            label: `${lot.catentry.productname} (${lot.catentry.partnumber || 'No SKU'})`,
                            value: lot.catentry.catentry_id.toString()
                        });
                    }
                });
                setComponentOptions(Array.from(itemMap.values()));
            } catch (err) {
                showToast("Failed to fetch components for selected store.", "error");
            } finally {
                setLoadingComponents(false);
            }
        };
        loadComponentsForStore();
    }, [currentBomLine.storeId, currentUser]);

    useEffect(() => {
        if (!storeId) return;
        const loadMasterData = async () => {
            setLoading(true);
            try {
                const payload = { ...currentUser, store_id: parseInt(storeId, 10), offset: 0, limit: 10000 };
                // const [services, inventory, workcenters] = await Promise.all([
                //     fetchServicesForStoreSale(payload).catch(() => []),
                //     // fetchInventoryForStoreSales(payload).catch(() => []),
                //     fetchTargetsForProduction(payload).catch(() => []),
                //     client.fetchData(`/manufacturing/work-centers/${storeId}`).catch(() => [])
                // ]);

                // const combined = [...services, ...inventory];
                // const itemMap = new Map();
                // combined.forEach(lot => {
                //     if (lot?.catentry) {
                //         itemMap.set(lot.catentry.catentry_id, {
                //             label: `${lot.catentry.productname} (${lot.catentry.partnumber || 'No SKU'})`,
                //             value: lot.catentry.catentry_id.toString()
                //         });
                //     }
                // });
                // setProductOptions(Array.from(itemMap.values()));

                const [targets, workcenters, assignedStores] = await Promise.all([
                    fetchTargetsForProduction(payload).catch(() => []),
                    client.fetchData(`/manufacturing/work-centers/${storeId}`).catch(() => []),
                    fetchStoresWhereStaffAssigned(currentUser).catch(() => [])
                ]);

                setAllStores((assignedStores || []).map(s => ({
                    label: s.identifier || `Store ${s.store_id}`,
                    value: s.store_id.toString()
                })));

                const itemMap = new Map();
                (Array.isArray(targets) ? targets : []).forEach(lot => {
                    if (lot?.catentry) {
                        itemMap.set(lot.catentry.catentry_id, {
                            label: `${lot.catentry.productname} (${lot.catentry.partnumber || 'No SKU'})`,
                            value: lot.catentry.catentry_id.toString()
                        });
                    }
                });
                setProductOptions(Array.from(itemMap.values()));

                const wcList = Array.isArray(workcenters) ? workcenters : (workcenters?.workcenters || []);
                setWorkcenterOptions(wcList.map(w => ({
                    label: `${w.name} (${w.resource_type})`,
                    value: w.workcenter_id.toString()
                })));
            } catch (err) {
                showToast("Failed to initialize engineering master data.", "error");
            } finally {
                setLoading(false);
            }
        };
        loadMasterData();
    }, [storeId, currentUser]);

    // const handleAddBomLine = () => {
    //     if (!currentBomLine.componentCatentryId || !currentBomLine.quantity) {
    //         showToast("Select a component and quantity.", "error");
    //         return;
    //     }
    //     setSpecData(prev => ({
    //         ...prev,
    //         bomLines: [...prev.bomLines, {
    //             ...currentBomLine,
    //             quantity: parseFloat(currentBomLine.quantity),
    //             scrapFactor: parseFloat(currentBomLine.scrapFactor || 0) / 100
    //         }]
    //     }));
    //     setCurrentBomLine({ componentCatentryId: '', quantity: '', unitMeasure: 'PCE', scrapFactor: '0' });
    // };

    const handleAddBomLine = () => {
        if (!currentBomLine.storeId || !currentBomLine.componentCatentryId || !currentBomLine.quantity) {
            showToast("Select a store, component, and quantity.", "error");
            return;
        }
        setSpecData(prev => ({
            ...prev,
            bomLines: [...prev.bomLines, {
                ...currentBomLine,
                quantity: parseFloat(currentBomLine.quantity),
                scrapFactor: parseFloat(currentBomLine.scrapFactor || 0) / 100
            }]
        }));
        setCurrentBomLine({ storeId: '', componentCatentryId: '', quantity: '', unitMeasure: 'PCE', scrapFactor: '0' });
    };

    const handleAddOperation = () => {
        if (!currentOp.workcenterId || !currentOp.sequence) {
            showToast("Select a work center and sequence index.", "error");
            return;
        }
        setSpecData(prev => ({
            ...prev,
            routingOperations: [...prev.routingOperations, {
                ...currentOp,
                sequence: parseInt(currentOp.sequence, 10),
                setupTime: currentOp.setupTime ? parseFloat(currentOp.setupTime) : 0,
                runTimePerUnit: currentOp.runTimePerUnit ? parseFloat(currentOp.runTimePerUnit) : 0,
                laborCostPerHour: currentOp.laborCostPerHour ? parseFloat(currentOp.laborCostPerHour) : 0
            }].sort((a, b) => a.sequence - b.sequence)
        }));
        setCurrentOp({ workcenterId: '', sequence: '', description: '', setupTime: '', runTimePerUnit: '', laborCostPerHour: '' });
    };

    const handleSaveBlueprint = async () => {
        if (!specData.catentryId || specData.bomLines.length === 0 || specData.routingOperations.length === 0) {
            showToast("Target product, BOM lines, and routing operations are required.", "error");
            return;
        }
        setSubmitting(true);
        try {
            const selectedProduct = productOptions.find(p => p.value === specData.catentryId);
            const selectedStore = allStores.find(s => s.value === storeId.toString());

            const productName = selectedProduct ? selectedProduct.label : `Product #${specData.catentryId}`;
            const storeName = selectedStore ? selectedStore.label : `Store #${storeId}`;

            const dynamicBomDescription = specData.bomDescription
                || `${productName} - ${storeName} (BOM v${specData.bomVersion})`;
            const dynamicRoutingDescription = specData.routingDescription
                || `${productName} - ${storeName} (Routing Rev ${specData.routingVersion})`;

            const payload = {
                catentry_id: parseInt(specData.catentryId, 10),
                store_id: parseInt(storeId, 10),
                bom_header: {
                    version: parseInt(specData.bomVersion, 10),
                    description: dynamicBomDescription,
                    is_active: true,
                    // lines: specData.bomLines.map((line, idx) => ({
                    //     component_catentry_id: parseInt(line.componentCatentryId, 10),
                    //     quantity: line.quantity,
                    //     unit_measure: line.unitMeasure,
                    //     scrap_factor: line.scrapFactor,
                    //     sequence: idx + 1
                    //     // TODO insert description here, perhaps?
                    // }))
                    lines: specData.bomLines.map((line, idx) => {
                        const compOption = componentOptions.find(opt => opt.value === line.componentCatentryId.toString())
                            || productOptions.find(opt => opt.value === line.componentCatentryId.toString());
                        const productName = compOption ? compOption.label.replace(/\s*\([^)]*\)$/, '') : null;

                        return {
                            component_catentry_id: parseInt(line.componentCatentryId, 10),
                            source_store_id: parseInt(line.storeId, 10),
                            quantity: line.quantity,
                            unit_measure: line.unitMeasure,
                            scrap_factor: line.scrapFactor,
                            sequence: idx + 1,
                            description: productName
                        };
                    })
                },
                routing_header: {
                    version: parseInt(specData.routingVersion, 10),
                    description: dynamicRoutingDescription,
                    is_active: true,
                    operations: specData.routingOperations.map(op => ({
                        workcenter_id: parseInt(op.workcenterId, 10),
                        sequence: op.sequence,
                        description: op.description || null,
                        setup_time: op.setupTime,
                        run_time_per_unit: op.runTimePerUnit,
                        labor_cost_per_hour: op.laborCostPerHour
                    }))
                }
            };
            // console.log(payload)
            await client.postData("/manufacturing/create-bom-routing-headers", payload);
            showToast("Engineering blueprint saved successfully.", "success");
            // TODO: perhaps clear the form after successful submission?
            setSpecData({
                catentryId: '',
                bomVersion: 1,
                bomDescription: null,
                routingVersion: 1,
                routingDescription: null,
                bomLines: [],
                routingOperations: []
            });
        } catch (err) {
            showToast(err.response?.data?.detail || "Failed to commit blueprint.", "error");
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) return <div className="p-8 text-center text-zinc-500"><Loader2 className="animate-spin inline mr-2 text-blue-600" /> Loading specification matrices...</div>;

    return (
        <div className="space-y-6">
            <Card className="p-6 border-zinc-200 shadow-sm bg-white">
                <div className="border-b border-zinc-100 pb-4 mb-4 flex justify-between items-center">
                    <div>
                        <h2 className="text-lg font-bold text-zinc-900">Configure Master Product Specs</h2>
                        <p className="text-xs text-zinc-500">Bind Bills of Materials (BOM) and execution routings to a product target.</p>
                    </div>
                    <Button onClick={handleSaveBlueprint} disabled={submitting} className="bg-blue-600 hover:bg-blue-700 text-white text-xs px-4 py-2">
                        {submitting ? <Loader2 size={14} className="animate-spin mr-1" /> : <Save size={14} className="mr-1 inline" />} Save Blueprint
                    </Button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <SearchableSelectBox
                        label="Target Manufactured Finished Good"
                        options={productOptions}
                        value={specData.catentryId}
                        onChange={(e) => setSpecData(p => ({ ...p, catentryId: e.target.value }))}
                        placeholder="Select target product..."
                    />
                    <div className="grid grid-cols-2 gap-2">
                        <Input label="BOM Rev" type="number" value={specData.bomVersion} onChange={(e) => setSpecData(p => ({ ...p, bomVersion: e.target.value }))} />
                        <Input label="Routing Rev" type="number" value={specData.routingVersion} onChange={(e) => setSpecData(p => ({ ...p, routingVersion: e.target.value }))} />
                    </div>
                </div>

                {/* Section 1: Bill of Materials */}
                <div className="space-y-3 mb-6">
                    <h3 className="text-sm font-semibold text-zinc-800 flex items-center gap-1.5">
                        <Layers size={16} className="text-blue-600" /> Bill of Materials (BOM) Component Lines
                    </h3>
                    {/* <div className="p-3 bg-zinc-50 rounded-lg grid grid-cols-1 md:grid-cols-12 gap-2 items-end border border-zinc-200">
                        <div className="md:col-span-5">
                            <SearchableSelectBox label="Component" options={productOptions} value={currentBomLine.componentCatentryId} onChange={(e) => setCurrentBomLine(p => ({ ...p, componentCatentryId: e.target.value }))} />
                        </div>
                        <div className="md:col-span-2">
                            <Input label="Qty" type="number" value={currentBomLine.quantity} onChange={(e) => setCurrentBomLine(p => ({ ...p, quantity: e.target.value }))} />
                        </div>
                        <div className="md:col-span-2">
                            <Input label="Scrap %" type="number" value={currentBomLine.scrapFactor} onChange={(e) => setCurrentBomLine(p => ({ ...p, scrapFactor: e.target.value }))} />
                        </div>
                        <div className="md:col-span-3">
                            <Button onClick={handleAddBomLine} className="w-full bg-zinc-900 text-white text-xs py-2 flex gap-2 items-center mb-4"><Plus size={14} /> Add Line</Button>
                        </div>
                    </div> */}
                    <div className="p-3 bg-zinc-50 rounded-lg grid grid-cols-1 md:grid-cols-12 gap-2 items-end border border-zinc-200">
                        <div className="md:col-span-3">
                            <SearchableSelectBox
                                label="Store"
                                options={allStores}
                                value={currentBomLine.storeId}
                                onChange={(e) => setCurrentBomLine(p => ({ ...p, storeId: e.target.value, componentCatentryId: '' }))}
                                placeholder="Select Store..."
                            />
                        </div>
                        <div className="md:col-span-4">
                            <SearchableSelectBox
                                label="Component"
                                options={componentOptions}
                                value={currentBomLine.componentCatentryId}
                                onChange={(e) => setCurrentBomLine(p => ({ ...p, componentCatentryId: e.target.value }))}
                                placeholder={loadingComponents ? "Loading..." : "Select component..."}
                                disabled={!currentBomLine.storeId || loadingComponents}
                            />
                        </div>
                        <div className="md:col-span-2">
                            <Input label="Qty" type="number" value={currentBomLine.quantity} onChange={(e) => setCurrentBomLine(p => ({ ...p, quantity: e.target.value }))} />
                        </div>
                        <div className="md:col-span-1">
                            <Input label="Scrap %" type="number" value={currentBomLine.scrapFactor} onChange={(e) => setCurrentBomLine(p => ({ ...p, scrapFactor: e.target.value }))} />
                        </div>
                        <div className="md:col-span-2">
                            <Button onClick={handleAddBomLine} className="w-full bg-zinc-900 text-white text-xs py-2 flex gap-2 items-center mb-4"><Plus size={14} /> Add Line</Button>
                        </div>
                    </div>

                    <div className="border rounded-lg overflow-hidden text-xs">
                        <table className="w-full text-left">
                            <thead className="bg-zinc-100 text-zinc-600">
                                <tr>
                                    <th className="p-2">Store</th>
                                    <th className="p-2">Component</th>
                                    <th className="p-2">Quantity</th>
                                    <th className="p-2">Scrap Factor</th>
                                    <th className="p-2 text-right">Action</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-zinc-100">
                                {specData.bomLines.length === 0 ? (
                                    <tr><td colSpan="4" className="p-3 text-center text-zinc-400">No BOM lines defined.</td></tr>
                                ) : (
                                    specData.bomLines.map((line, idx) => (
                                        <tr key={idx}>
                                            <td className="p-2">{allStores.find(s => s.value == line.storeId)?.label || `Store #${line.storeId}`}</td>
                                            <td className="p-2">{productOptions.find(p => p.value === line.componentCatentryId)?.label}</td>
                                            <td className="p-2">{line.quantity} {line.unitMeasure}</td>
                                            <td className="p-2">{(line.scrapFactor * 100).toFixed(1)}%</td>
                                            <td className="p-2 text-right">
                                                <button onClick={() => setSpecData(p => ({ ...p, bomLines: p.bomLines.filter((_, i) => i !== idx) }))} className="text-red-500 hover:text-red-700"><Trash2 size={14} /></button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Section 2: Factory Routing Steps */}
                <div className="space-y-3">
                    <div className="flex justify-between items-center">
                        <h3 className="text-sm font-semibold text-zinc-800 flex items-center gap-1.5">
                            <Settings size={16} className="text-blue-600" /> Sequence Routing Operations
                        </h3>
                        <Button onClick={() => setIsWcModalOpen(true)} className="text-xs bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 py-1 px-2">
                            <PlusCircle size={14} className="mr-1 inline" /> New Workcenter
                        </Button>
                    </div>

                    <div className="p-3 bg-zinc-50 rounded-lg grid grid-cols-1 md:grid-cols-12 gap-2 items-end border border-zinc-200">
                        <div className="md:col-span-2">
                            <Input label="Seq #" type="number" value={currentOp.sequence} onChange={(e) => setCurrentOp(p => ({ ...p, sequence: e.target.value }))} placeholder="10" />
                        </div>
                        <div className="md:col-span-5">
                            <SearchableSelectBox label="Work Center" options={workcenterOptions} value={currentOp.workcenterId} onChange={(e) => setCurrentOp(p => ({ ...p, workcenterId: e.target.value }))} />
                        </div>
                        <div className="md:col-span-5">
                            <Input label="Operation Description" value={currentOp.description} onChange={(e) => setCurrentOp(p => ({ ...p, description: e.target.value }))} placeholder="e.g. Precision CNC Milling" />
                        </div>
                        <div className="md:col-span-4">
                            <Input label="Setup Hrs" type="number" value={currentOp.setupTime} onChange={(e) => setCurrentOp(p => ({ ...p, setupTime: e.target.value }))} />
                        </div>
                        <div className="md:col-span-4">
                            <Input label="Run Hrs/Unit" type="number" value={currentOp.runTimePerUnit} onChange={(e) => setCurrentOp(p => ({ ...p, runTimePerUnit: e.target.value }))} />
                        </div>
                        <div className="md:col-span-4">
                            <Button onClick={handleAddOperation} className="w-full bg-zinc-900 text-white text-xs py-2 mb-4 flex items-center gap-2"><Plus size={14} /> Commit Step</Button>
                        </div>
                    </div>

                    <div className="border rounded-lg overflow-hidden text-xs">
                        <table className="w-full text-left">
                            <thead className="bg-zinc-100 text-zinc-600">
                                <tr>
                                    <th className="p-2">Seq</th>
                                    <th className="p-2">Work Center</th>
                                    <th className="p-2">Operation Details</th>
                                    <th className="p-2 text-right">Action</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-zinc-100">
                                {specData.routingOperations.length === 0 ? (
                                    <tr><td colSpan="4" className="p-3 text-center text-zinc-400">No routing steps mapped.</td></tr>
                                ) : (
                                    specData.routingOperations.map((op, idx) => (
                                        <tr key={idx}>
                                            <td className="p-2 font-bold text-blue-600">#{op.sequence}</td>
                                            <td className="p-2">{workcenterOptions.find(w => w.value === op.workcenterId)?.label}</td>
                                            <td className="p-2">{op.description || 'Standard Process Phase'}</td>
                                            <td className="p-2 text-right">
                                                <button onClick={() => setSpecData(p => ({ ...p, routingOperations: p.routingOperations.filter((_, i) => i !== idx) }))} className="text-red-500 hover:text-red-700"><Trash2 size={14} /></button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </Card>

            {/* Modal to create Work Center on the fly */}
            {/* <Modal isOpen={isWcModalOpen} onClose={() => setIsWcModalOpen(false)} title="Register Plant Work Center"> */}
            {/* <Modal isOpen={isWcModalOpen} onClose={() => setIsWcModalOpen(false)} title="Register Plant Work Center">
                <div className="space-y-3 text-xs">
                    <Input required label="Work Center Name" value={newWc.name} onChange={(e) => setNewWc(p => ({ ...p, name: e.target.value }))} placeholder="Line 1 Assembly" />
                    <SearchableSelectBox label="Resource Type" options={WORKCENTER_RESOURCE_TYPES} value={newWc.resource_type} onChange={(e) => setNewWc(p => ({ ...p, resource_type: e.target.value }))} />
                    <div className="grid grid-cols-2 gap-2">
                        <Input label="Capacity / Hr" type="number" value={newWc.capacity_per_hour} onChange={(e) => setNewWc(p => ({ ...p, capacity_per_hour: e.target.value }))} />
                        <Input label="Cost / Hr ($)" type="number" value={newWc.cost_per_hour} onChange={(e) => setNewWc(p => ({ ...p, cost_per_hour: e.target.value }))} />
                    </div>
                    <Button onClick={async () => {
                        try {
                            const payload = { ...newWc, store_id: parseInt(storeId, 10), is_active: true }
                            const res = await client.postData("/manufacturing/new-work-center", payload);
                            showToast("Work center created.", "success");
                            setWorkcenterOptions(p => [...p, { label: `${newWc.name} (${newWc.resource_type})`, value: res.workcenter_id.toString() }]);
                            setIsWcModalOpen(false);
                        } catch (err) {
                            showToast("Error creating work center.", "error");
                        }
                    }} className="w-full bg-blue-600 text-white py-2">Deploy Work Center</Button>
                </div>
            </Modal> */}
            <Modal isOpen={isWcModalOpen} onClose={() => setIsWcModalOpen(false)} title="Register Plant Work Centers">
                <div className="space-y-4 text-xs max-h-[70vh] overflow-y-auto p-1">
                    {newWcList.map((wc, idx) => (
                        <div key={idx} className="p-3 border border-zinc-200 rounded-lg bg-zinc-50/50 space-y-2 relative">
                            <div className="flex justify-between items-center mb-1">
                                <span className="font-bold text-zinc-700">Work Center #{idx + 1}</span>
                                {newWcList.length > 1 && (
                                    <button
                                        type="button"
                                        onClick={() => handleRemoveWcRow(idx)}
                                        className="text-red-500 hover:text-red-700 text-xs font-semibold"
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                )}
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                <Input
                                    required
                                    label="Work Center Name"
                                    value={wc.name}
                                    onChange={(e) => handleWcRowChange(idx, 'name', e.target.value)}
                                    placeholder="e.g. Line 1 Assembly"
                                />
                                <SearchableSelectBox
                                    label="Resource Type"
                                    options={WORKCENTER_RESOURCE_TYPES}
                                    value={wc.resource_type}
                                    onChange={(e) => handleWcRowChange(idx, 'resource_type', e.target.value)}
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-2">
                                <Input
                                    label="Capacity / Hr"
                                    type="number"
                                    value={wc.capacity_per_hour}
                                    onChange={(e) => handleWcRowChange(idx, 'capacity_per_hour', e.target.value)}
                                />
                                <Input
                                    label="Cost / Hr ($)"
                                    type="number"
                                    value={wc.cost_per_hour}
                                    onChange={(e) => handleWcRowChange(idx, 'cost_per_hour', e.target.value)}
                                />
                            </div>
                        </div>
                    ))}

                    <div className="flex justify-between items-center pt-2">
                        <Button
                            type="button"
                            onClick={handleAddWcRow}
                            className="bg-zinc-100 hover:bg-zinc-200 text-zinc-800 text-xs py-1.5 px-3 border border-zinc-300"
                        >
                            <Plus size={14} className="mr-1 inline" /> Add Another Line Item
                        </Button>

                        <Button
                            disabled={creatingWc}
                            onClick={async () => {
                                // Validation check
                                const invalid = newWcList.some(item => !item.name || !item.resource_type);
                                if (invalid) {
                                    showToast("Please specify a name and resource type for all work centers.", "error");
                                    return;
                                }

                                setCreatingWc(true);
                                try {
                                    // Construct payload conforming to CreateWorkcentersRequest
                                    const payload = {
                                        rows: newWcList.map(wc => ({
                                            store_id: parseInt(storeId, 10),
                                            name: wc.name,
                                            resource_type: wc.resource_type,
                                            capacity_per_hour: wc.capacity_per_hour ? parseFloat(wc.capacity_per_hour) : 0,
                                            cost_per_hour: wc.cost_per_hour ? parseFloat(wc.cost_per_hour) : 0,
                                            description: wc.description || null,
                                            is_active: true
                                        }))
                                    };

                                    // Send single batch post request
                                    const createdWorkcenters = await client.postData("/manufacturing/new-work-centers", payload);

                                    // Format created entries for workcenterOptions state
                                    const newOptions = (createdWorkcenters || []).map(wc => ({
                                        label: `${wc.name} (${wc.resource_type})`,
                                        value: (wc.workcenter_id || wc.id).toString()
                                    }));

                                    setWorkcenterOptions(prev => [...prev, ...newOptions]);
                                    showToast(`${newWcList.length} Work Center(s) deployed successfully.`, "success");

                                    // Reset modal list and close
                                    setNewWcList([{ name: '', resource_type: '', capacity_per_hour: '', cost_per_hour: '' }]);
                                    setIsWcModalOpen(false);
                                } catch (err) {
                                    showToast(err.response?.data?.detail || "Error creating work centers.", "error");
                                } finally {
                                    setCreatingWc(false);
                                }
                            }}
                            className="bg-blue-600 hover:bg-blue-700 text-white text-xs py-2 px-4"
                        >
                            {creatingWc ? <Loader2 size={14} className="animate-spin inline mr-1" /> : null}
                            Deploy All Work Centers ({newWcList.length})
                        </Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};